import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CHANNEL_PLATFORMS, PLATFORMS, POST_MIN_LEAD_MS, captionLength,
  type ChannelPlatform, type QueuedPost,
} from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { localAt } from "../studio/Publish.js";

/**
 * **Calendar → To approve** ([31](../../../../docs/31-posts-joogoos-drafts.md),
 * [ADR 0056](../../../../docs/adr/0056-a-post-joogoos-writes-is-a-draft-until-an-owner-approves-it-and-without-a-connector-the-owner-posts-it.md)).
 *
 * What waits for a person, in the order it is urgent:
 *
 * 1. **Due, to post by hand.** Approved posts no connector will publish. One press copies the text and opens the
 *    network's compose page; a second says it is up.
 * 2. **To approve.** Drafts, each with what it says, where it goes, and when. *Approve* is the owner's and it is
 *    what gives a draft its time; *Edit* and *Reject* are anybody's.
 * 3. **Later, by hand.** Approved and not due yet, so the week is visible and can still be taken back.
 *
 * Nothing on this screen publishes. The most it does is open a compose page in a new tab, where the owner presses
 * the network's own button.
 */

export const QUEUE = ["posts", "queue"];

/** What a person can write a draft for by hand: the networks that take words alone (the rest need a picture). */
const WRITABLE = CHANNEL_PLATFORMS.filter((p) => PLATFORMS[p].textAlone);

export function PostQueue() {
  const q = useQuery({ queryKey: QUEUE, queryFn: () => api.postQueue() });
  const [writing, setWriting] = useState(false);

  if (q.isPending) return <p className="text-sm text-faint">…</p>;
  if (!q.data) return <p className="text-sm text-faint">Nothing here yet.</p>;
  const { drafts, byHand, mayApprove } = q.data;
  const now = Date.now();
  const due = byHand.filter((p) => new Date(p.scheduledAt).getTime() <= now);
  const later = byHand.filter((p) => new Date(p.scheduledAt).getTime() > now);

  return (
    <div className="flex flex-col gap-6 max-w-[760px]">
      {due.length > 0 && (
        <section className="flex flex-col gap-2">
          <Heading title="Due — post it now" count={due.length} note="no account is connected for these, so they are yours to put up" />
          {due.map((p) => <ByHand key={p.id} post={p} mayApprove={mayApprove} />)}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <Heading title="To approve" count={drafts.length} note="nothing goes out until an owner approves it" />
          <button
            onClick={() => setWriting(true)}
            className="ml-auto px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium flex items-center gap-1"
          ><Icon name="plus" size={12} /> Write a post</button>
        </div>
        {writing ? <Composer onDone={() => setWriting(false)} /> : null}
        {drafts.length === 0 && !writing
          ? <p className="text-[12.5px] text-faint">Nothing is waiting for you.</p>
          : drafts.map((p) => <Draft key={p.id} post={p} mayApprove={mayApprove} />)}
      </section>

      {later.length > 0 && (
        <section className="flex flex-col gap-2">
          <Heading title="Coming up, by hand" count={later.length} note="approved; this page will ask for each when it is due" />
          {later.map((p) => <ByHand key={p.id} post={p} mayApprove={mayApprove} />)}
        </section>
      )}
    </div>
  );
}

function Heading({ title, count, note }: { title: string; count: number; note: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <h2 className="text-[13px] font-semibold">{title}</h2>
      {count > 0 && <span className="text-[11px] text-muted tabular-nums">{count}</span>}
      <span className="text-[11.5px] text-muted">{note}</span>
    </div>
  );
}

/** Refresh everything a change to a post can move: this queue, the calendar grid, and the count on the tab. */
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: QUEUE });
    void qc.invalidateQueries({ queryKey: ["calendar"] });
  };
}

/** How a post will go out, in a line: through an account, or by the owner's own hand. */
function goesOut(post: QueuedPost): string {
  if (!post.handOff) return `goes out on its own through ${post.accountName === PLATFORMS[post.platform].label ? `your ${post.accountName}` : post.accountName}`;
  return `by hand${post.handOff.as ? `, as ${post.handOff.as}` : ""}`;
}

function Chip({ platform }: { platform: ChannelPlatform }) {
  return <span className="px-1.5 py-0.5 rounded bg-line-2 text-ink-2 text-[10px] font-semibold uppercase tracking-[.05em]">{PLATFORMS[platform].label}</span>;
}

/** The count a network will make of these words, against what it keeps. Red when it will not keep them. */
function Count({ platform, text }: { platform: ChannelPlatform; text: string }) {
  const n = captionLength(platform, text), max = PLATFORMS[platform].captionMax;
  return <span className={`text-[11px] tabular-nums ${n > max ? "text-bad font-semibold" : "text-muted"}`}>{n.toLocaleString()} / {max.toLocaleString()}</span>;
}

/** A draft: read it, change it, say yes with a time, or say no. */
function Draft({ post, mayApprove }: { post: QueuedPost; mayApprove: boolean }) {
  const refresh = useRefresh();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(post.caption);
  // A proposed time already gone is replaced by the soonest sensible one, so Approve is never pre-filled with a
  // time the server will refuse.
  const [at, setAt] = useState(() => localAt(new Date(Math.max(new Date(post.scheduledAt).getTime(), Date.now() + 10 * 60_000))));

  const save = useMutation({
    mutationFn: () => api.editPost(post.id, { caption: text }),
    onSuccess: () => { setEditing(false); refresh(); },
  });
  const approve = useMutation({ mutationFn: () => api.approvePost(post.id, { scheduledAt: new Date(at).toISOString() }), onSuccess: refresh });
  const reject = useMutation({ mutationFn: () => api.rejectPost(post.id), onSuccess: refresh });
  const failed = save.error ?? approve.error ?? reject.error;
  const busy = save.isPending || approve.isPending || reject.isPending;

  return (
    <article className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5" aria-label={`Draft for ${PLATFORMS[post.platform].label}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Chip platform={post.platform} />
        <span className="text-[11.5px] text-muted">{goesOut(post)}</span>
        {editing ? <span className="ml-auto"><Count platform={post.platform} text={text} /></span> : null}
      </div>

      {editing
        ? (
          <textarea
            value={text} onChange={(e) => setText(e.target.value)} rows={Math.min(12, Math.max(3, text.split("\n").length + 1))}
            aria-label="What it says" className="px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px] leading-relaxed resize-y"
          />
        )
        : <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{post.caption}</p>}
      {/* What Joogoos drew it from, beside it: approving is reading the two side by side (31 §the drafter). */}
      {post.source ? <p className="text-[11.5px] text-muted">Written by Joogoos from “{post.source}”</p> : null}
      {post.thumbUrl ? <img src={post.thumbUrl} alt="" loading="lazy" className="w-28 rounded-lg border border-line object-cover" /> : null}

      <div className="flex flex-wrap items-center gap-1.5">
        {editing
          ? (
            <>
              <button onClick={() => save.mutate()} disabled={busy || text.trim() === ""} className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50">Save</button>
              <button onClick={() => { setEditing(false); setText(post.caption); }} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Cancel</button>
            </>
          )
          : (
            <>
              <input
                type="datetime-local" value={at} min={localAt(new Date(Date.now() + POST_MIN_LEAD_MS))}
                onChange={(e) => setAt(e.target.value)} aria-label="When it goes out"
                className="px-2 py-1 rounded-lg border border-line-2 bg-page text-[11.5px]"
              />
              <button
                onClick={() => approve.mutate()} disabled={!mayApprove || busy || !at}
                title={mayApprove ? undefined : "Only an owner of this business approves a post."}
                className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50"
              >Approve</button>
              <button onClick={() => setEditing(true)} disabled={busy} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Edit</button>
              <button onClick={() => reject.mutate()} disabled={busy} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Reject</button>
            </>
          )}
      </div>
      {!mayApprove && !editing ? <p className="text-[11.5px] text-muted">An owner of this business approves it.</p> : null}
      {failed ? <p className="text-xs text-bad">{String(failed)}</p> : null}
    </article>
  );
}

/**
 * An approved post nobody but the owner can publish. *Copy and open* puts the words on the clipboard and opens the
 * network in a new tab — prefilled where the network allows it, to paste into where it does not — and *It's posted*
 * says it is up, with its address if the owner has it to hand.
 */
function ByHand({ post, mayApprove }: { post: QueuedPost; mayApprove: boolean }) {
  const refresh = useRefresh();
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);
  const [url, setUrl] = useState("");
  const posted = useMutation({ mutationFn: () => api.postedByHand(post.id, url.trim() ? { url: url.trim() } : {}), onSuccess: refresh });
  const takeBack = useMutation({ mutationFn: () => api.cancelPost(post.id), onSuccess: refresh });
  const label = PLATFORMS[post.platform].label;
  const hand = post.handOff!;
  const at = new Date(post.scheduledAt);

  const open = () => {
    // The clipboard first, because the new tab takes the focus and a write after it can be refused.
    void navigator.clipboard?.writeText(post.caption).catch(() => {});
    setCopied(true);
    window.open(hand.url, "_blank", "noopener,noreferrer");
  };

  return (
    <article className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5" aria-label={`${label} post to make by hand`}>
      <div className="flex flex-wrap items-center gap-2">
        <Chip platform={post.platform} />
        <span className="text-[11.5px] text-muted">
          due {at.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          {hand.as ? ` · post as ${hand.as}` : ""}
        </span>
      </div>
      <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{post.caption}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={open} className="px-2.5 py-1 rounded-lg bg-coral text-white text-[11.5px] font-semibold flex items-center gap-1">
          <Icon name="copy" size={12} /> Copy and open {label}
        </button>
        {marking
          ? (
            <>
              <input
                value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Its address, if you have it"
                aria-label="Where it is" className="px-2 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] min-w-0 w-56"
              />
              <button onClick={() => posted.mutate()} disabled={posted.isPending} className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50">Done</button>
            </>
          )
          : (
            <button
              onClick={() => setMarking(true)} disabled={!mayApprove}
              title={mayApprove ? undefined : "Only an owner of this business says a post is up."}
              className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium disabled:opacity-50"
            >It's posted</button>
          )}
        <button onClick={() => takeBack.mutate()} disabled={takeBack.isPending} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Take it back</button>
      </div>
      {copied
        ? (
          <p className="text-[11.5px] text-muted">
            {hand.prefilled
              ? `Copied. ${label} opened with it written in — check the account, then post.`
              : `Copied. ${label} has no way to take the words in, so press Create there and paste.`}
          </p>
        )
        : null}
      {posted.error ?? takeBack.error ? <p className="text-xs text-bad">{String(posted.error ?? takeBack.error)}</p> : null}
    </article>
  );
}

/** A draft written by a person, which then waits for an owner like one Joogoos wrote. */
function Composer({ onDone }: { onDone: () => void }) {
  const refresh = useRefresh();
  const [platform, setPlatform] = useState<ChannelPlatform>("linkedin");
  const [text, setText] = useState("");
  const write = useMutation({
    mutationFn: () => api.draftPost({ platform, caption: text }),
    onSuccess: () => { refresh(); onDone(); },
  });
  const over = captionLength(platform, text) > PLATFORMS[platform].captionMax;

  return (
    <div className="rounded-xl border border-line-2 bg-panel p-3.5 flex flex-col gap-2.5" role="group" aria-label="Write a post">
      <div className="flex flex-wrap items-center gap-1.5">
        {WRITABLE.map((p) => (
          <button
            key={p} onClick={() => setPlatform(p)} aria-pressed={platform === p}
            className={`px-2.5 py-1 rounded-lg text-[11.5px] font-medium ${platform === p ? "bg-ink text-white" : "border border-line-2 bg-page"}`}
          >{PLATFORMS[p].label}</button>
        ))}
        <span className="ml-auto"><Count platform={platform} text={text} /></span>
      </div>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={5} aria-label="What it says"
        placeholder={platform === "x" ? "Short: X keeps 280." : "Plain, short, concrete sentences."}
        className="px-3 py-2 rounded-lg border border-line-2 bg-page text-[13px] leading-relaxed resize-y"
      />
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => write.mutate()} disabled={write.isPending || !text.trim() || over}
          className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50"
        >Save as a draft</button>
        <button onClick={onDone} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">Cancel</button>
      </div>
      {write.error ? <p className="text-xs text-bad">{String(write.error)}</p> : null}
    </div>
  );
}
